/** The lesson player: activity sequence → score → complete → results. */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError } from "../../api/http";
import * as api from "../../api/learning";
import { Button, Card, Spinner } from "../../components/ui";
import { useT } from "../../i18n";
import { ActivityRenderer, SCORED_TYPES, type ActivityResult } from "./activities";
import { AskDoubt } from "../../components/AskDoubt";
import { SaveToNotebook } from "../notebook/SaveToNotebook";

export function scoreFrom(results: ActivityResult[]): number {
  const scored = results.filter((r) => r !== null) as boolean[];
  if (scored.length === 0) return 100; // purely informational lesson
  return Math.round((scored.filter(Boolean).length / scored.length) * 100);
}

export function LessonPlayerPage() {
  const t = useT();
  const navigate = useNavigate();
  const { lessonId = "" } = useParams();
  const [lesson, setLesson] = useState<api.LessonDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [results, setResults] = useState<ActivityResult[]>([]);
  const [finished, setFinished] = useState<api.LessonResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .lessonDetail(lessonId)
      .then((l) => {
        if (!cancelled) setLesson(l);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : "error");
      });
    return () => {
      cancelled = true;
    };
  }, [lessonId]);

  const onActivityDone = async (correct: ActivityResult) => {
    const nextResults = [...results, correct];
    setResults(nextResults);
    if (lesson && index + 1 < lesson.activities.length) {
      setIndex(index + 1);
      return;
    }
    // Last activity → submit the score.
    try {
      const result = await api.completeLesson(lessonId, scoreFrom(nextResults));
      setFinished(result);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "error");
    }
  };

  if (error) {
    return (
      <Card>
        <p role="alert" className="text-sm text-danger">{error}</p>
        <Button variant="secondary" onClick={() => navigate(-1)} className="mt-3">
          {t("player.exit")}
        </Button>
      </Card>
    );
  }

  if (!lesson) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner size={28} />
      </div>
    );
  }

  if (finished) {
    return (
      <Card>
        <div className="py-6 text-center">
          <p className="text-4xl" aria-hidden>🎉</p>
          <h1 className="mt-2 text-2xl font-extrabold text-ink">{t("player.finish.title")}</h1>
          <p className="mt-3 text-lg">
            {t("player.finish.score")}:{" "}
            <strong data-testid="final-score">{finished.score_percent}%</strong>
          </p>
          <p className="text-sm text-muted">
            +{finished.xp_earned} {t("learn.xp")} · {lesson.title}
          </p>
          <Link to="/learn" className="mt-5 inline-block">
            <Button>{t("player.finish.backToCourse")}</Button>
          </Link>
        </div>
      </Card>
    );
  }

  const total = lesson.activities.length;
  const activity = lesson.activities[index];
  const scoredBadge = SCORED_TYPES.has(activity.type);

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-ink">{lesson.title}</h1>
        <Button variant="ghost" onClick={() => navigate(-1)}>
          {t("player.exit")}
        </Button>
      </div>
      <div
        role="progressbar"
        aria-valuenow={index + 1}
        aria-valuemin={1}
        aria-valuemax={total}
        aria-label={`activity ${index + 1} of ${total}`}
        className="h-2 overflow-hidden rounded-full bg-line"
      >
        <div
          className="h-full rounded-full bg-brand transition-all"
          style={{ width: `${((index + 1) / total) * 100}%` }}
        />
      </div>
      <Card>
        {scoredBadge && (
          <p className="mb-2 text-xs font-semibold uppercase text-brand-dark">
            {index + 1}/{total}
          </p>
        )}
        {/* key= resets internal activity state on advance */}
        <ActivityRenderer
          key={activity.id}
          type={activity.type}
          payload={activity.payload}
          onDone={onActivityDone}
        />
      </Card>
      {/* Two things a learner reaches for mid-lesson: keeping something, and
          asking about something. */}
      {lesson && (
        <SaveToNotebook
          label={lesson.title}
          path={`/learn/lessons/${lessonId}`}
        />
      )}
      {/* Stuck on a word or a rule? Ask in Hindi, Punjabi or English and get
          the answer back in the same language. It used to live on the Speak
          page only, where a learner mid-lesson would never find it. */}
      <AskDoubt />
    </div>
  );
}
